import type { CSSProperties } from 'react'
import { getRacerIdentity } from '../game/racer-config'
import type { RacerProgress, SimulationState } from '../game/types'
import { formatOrdinal } from './race-format'

interface ResultsScreenProps {
  state: SimulationState
  onRestart: () => void
}

interface RankedResult {
  vehicleId: string
  place: number
  name: string
  accent: string
  progress: RacerProgress | undefined
  record: string
}

export function formatRaceTime(seconds: number | null): string {
  if (seconds === null) return '--:--.---'
  const totalMilliseconds = Math.max(0, Math.round(seconds * 1_000))
  const minutes = Math.floor(totalMilliseconds / 60_000)
  const secondsPart = Math.floor((totalMilliseconds % 60_000) / 1_000)
  const milliseconds = totalMilliseconds % 1_000
  return `${minutes.toString().padStart(2, '0')}:${secondsPart.toString().padStart(2, '0')}.${milliseconds
    .toString()
    .padStart(3, '0')}`
}

function formatRecord(progress: RacerProgress | undefined): string {
  if (progress?.recordStatus !== 'finished' || progress.finishedAt === null) return 'UNRECORDED'
  return formatRaceTime(progress.finishedAt)
}

function getRankedResults(state: SimulationState): RankedResult[] {
  return state.ranking.map((vehicleId, index) => {
    const identity = getRacerIdentity(vehicleId)
    const vehicle = state.vehicles.find((candidate) => candidate.id === vehicleId)
    const progress = state.race.progress[vehicleId]
    return {
      vehicleId,
      place: index + 1,
      name: vehicle?.displayName ?? identity.name,
      accent: identity.accent,
      progress,
      record: formatRecord(progress),
    }
  })
}

export function ResultsScreen({ state, onRestart }: ResultsScreenProps) {
  const playerId = state.race.playerId
  const player = state.vehicles.find((vehicle) => vehicle.id === playerId)
  const playerProgress = state.race.progress[playerId]
  const ranked = getRankedResults(state)
  const podium = ranked.slice(0, 3)
  const playerResult = ranked.find((result) => result.vehicleId === playerId)

  return (
    <section className="results-screen" role="dialog" aria-modal="true" aria-labelledby="results-title">
      <header className="results-header">
        <div>
          <p className="eyebrow">OFFICIAL NIGHT GRID RESULTS</p>
          <h2 id="results-title">NIGHT PODIUM</h2>
        </div>
        <div className="player-result-stamp">
          <small>YOUR RESULT</small>
          <strong>{formatOrdinal(playerResult?.place ?? ranked.length)}</strong>
          <span>{playerResult?.record ?? 'UNRECORDED'}</span>
        </div>
      </header>

      <section className="podium" aria-label="Top three finishers">
        {podium.map((result) => (
          <article
            key={result.vehicleId}
            className={`podium-card podium-card--place-${result.place} ${
              result.vehicleId === playerId ? 'podium-card--player' : ''
            }`}
            data-testid="podium-card"
            style={{ '--racer-accent': result.accent } as CSSProperties}
          >
            <small>{formatOrdinal(result.place)}</small>
            <strong>{result.name}</strong>
            <time>{result.record}</time>
            <span className="podium-card__step" aria-hidden="true">
              {result.place.toString().padStart(2, '0')}
            </span>
          </article>
        ))}
      </section>

      <section className="official-results" aria-labelledby="official-results-title">
        <div className="official-results__header">
          <h3 id="official-results-title">OFFICIAL RESULTS</h3>
          <span>3 LAPS {'//'} FINAL CLASSIFICATION</span>
        </div>
        <ol>
          {ranked.map((result) => (
            <li
              key={result.vehicleId}
              className={result.vehicleId === playerId ? 'official-result-row--player' : undefined}
              data-testid="official-result-row"
              style={{ '--racer-accent': result.accent } as CSSProperties}
            >
              <b>{formatOrdinal(result.place)}</b>
              <span>{result.name}</span>
              <time data-unrecorded={result.record === 'UNRECORDED' || undefined}>{result.record}</time>
            </li>
          ))}
        </ol>
      </section>

      <div className="result-grid result-grid--ceremony">
        <div>
          <span>BEST LAP</span>
          <strong>{formatRaceTime(playerProgress?.bestLap ?? null)}</strong>
        </div>
        <div>
          <span>LONGEST DRIFT</span>
          <strong>{(player?.drift.longest ?? 0).toFixed(2)} S</strong>
        </div>
        <div>
          <span>DRIVER HITS</span>
          <strong>
            {state.stats.hits.toString().padStart(2, '0')} / {state.stats.shotsFired.toString().padStart(2, '0')}
          </strong>
        </div>
        <div>
          <span>ACCURACY</span>
          <strong>{Math.round((state.stats.hits / Math.max(1, state.stats.shotsFired)) * 100)}%</strong>
        </div>
      </div>

      <button className="primary-button results-restart" type="button" onClick={onRestart}>
        Race again
      </button>
    </section>
  )
}
